/*
  YFF cart rules — gifting minimum, alcohol-with-flowers rule, alcohol share cap,
  18+ confirmation and pre-order notice.

  Reads the JSON emitted by snippets/cart-rules-data.liquid (rendered inside the
  cart drawer and the cart page), shows messages, disables the checkout buttons
  while a rule is broken, and asks for 18+ confirmation before checkout when
  alcohol is in the cart.

  NOTE: this is a storefront UX layer. Anyone typing /checkout directly bypasses
  it — for hard enforcement add a Cart & Checkout Validation app (see the
  research brief). Keep both in sync.
*/
(function () {
  'use strict';

  var AGE_KEY = 'yffAgeConfirmed';
  var CHECKOUT_SELECTORS = '#CartDrawer-Checkout, #checkout, .cart__checkout-button';

  // Zapiet only lets checkout through once a postcode and delivery date are chosen. Without them the
  // checkout page has no shipping rates ("Shipping not available"), so never navigate past its check.
  function zapietAllowsCheckout() {
    try {
      if (window.Zapiet && Zapiet.Widget && typeof Zapiet.Widget.checkoutEnabled === 'function') {
        return !!Zapiet.Widget.checkoutEnabled();
      }
    } catch (e) {}
    return true;
  }

  // Bring Zapiet's postcode/date widget (and its own error text) into view and shake it.
  function nudgeZapiet() {
    var widget = document.querySelector('#cart-tabs-2 #storePickupApp') || document.querySelector('#storePickupApp');
    if (!widget) return;
    try { widget.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { widget.scrollIntoView(); }
    widget.classList.remove('yff-shake'); void widget.offsetWidth; widget.classList.add('yff-shake');
    var pc = widget.querySelector('#deliveryGeoSearchField, input[type="text"]');
    if (pc && !pc.value) { setTimeout(function () { pc.focus(); }, 400); }
  }

  // wait for any cart-attribute update still in flight (gift fields) before leaving the page
  function goToCheckout() {
    if (!zapietAllowsCheckout()) { nudgeZapiet(); return; }
    var pending = window.YFFPendingCartUpdates ? window.YFFPendingCartUpdates() : Promise.resolve();
    pending.then(function () { window.location = '/checkout'; }, function () { window.location = '/checkout'; });
  }

  // Zapiet renders its widget into #storePickupApp once, on page load. Every drawer re-render (add to
  // cart, quantity change, the jQuery .load() refresh) replaces that element with the empty placeholder
  // from the snippet, and Zapiet's own DOM watcher skips it because a #storePickupApp already exists —
  // leaving a first-time visitor with no postcode/date fields. Ask Zapiet to start again when that happens.
  var zapietLastStart = 0;
  function remountZapiet() {
    var app = document.querySelector('cart-drawer #storePickupApp');
    if (!app || app.childElementCount || !window.Zapiet || typeof Zapiet.start !== 'function' || !window.ZapietCachedSettings) return;
    var now = Date.now();
    if (now - zapietLastStart < 1500) return;
    zapietLastStart = now;
    try { Zapiet.start(window.ZapietCachedSettings); } catch (e) {}
  }

  function readData() {
    var el = document.getElementById('yff-cart-rules-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency: currency || 'AUD', maximumFractionDigits: 0 }).format(cents / 100);
    } catch (e) { return '$' + Math.round(cents / 100); }
  }

  // Zapiet writes Delivery-Date as YYYY/MM/DD; accept YYYY-MM-DD and DD/MM/YYYY too. Returns a UTC-midnight timestamp.
  function parseDate(s) {
    if (!s) return null;
    var m = String(s).trim().match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})/);
    if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3]);
    m = String(s).trim().match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})/);
    if (m) return Date.UTC(+m[3], +m[2] - 1, +m[1]);
    var t = Date.parse(s);
    return isNaN(t) ? null : t;
  }

  function evaluate(d) {
    var errors = [], infos = [];
    if (!d || !d.items || !d.items.length) return { ok: true, errors: errors, infos: infos, needsAge: false };
    var anchors = d.items.filter(function (i) { return i.anchor; });
    var alcohol = d.items.filter(function (i) { return i.alcohol; });

    if (d.alcoholRule && alcohol.length && !anchors.length) errors.push(d.messages.alcohol);

    if (d.minOrder > 0 && !anchors.length && d.total < d.minOrder * 100) {
      errors.push(d.messages.min.replace('[min]', money(d.minOrder * 100, d.currency)));
    }

    // share cap only matters once there IS a flower/gift-box in the cart (otherwise the rule above already fires)
    if (d.alcoholMaxShare > 0 && alcohol.length && anchors.length && d.total > 0) {
      var share = (d.alcoholTotal / d.total) * 100;
      if (share > d.alcoholMaxShare) errors.push(d.messages.share.replace('[share]', d.alcoholMaxShare));
    }

    if (d.items.some(function (i) { return i.preorder; })) {
      infos.push(d.messages.preorder.replace('[from]', d.preorderFrom).replace('[by]', d.preorderBy));
      // Zapiet can't restrict dates per product on this plan, so block a delivery date before the pre-order start here
      var chosen = parseDate(d.deliveryDate), from = parseDate(d.preorderFromIso);
      if (chosen && from && chosen < from) errors.push(d.messages.preorderDate.replace('[from]', d.preorderFrom));
    }

    var ageOk = false;
    try { ageOk = sessionStorage.getItem(AGE_KEY) === '1'; } catch (e) {}
    return {
      ok: errors.length === 0, errors: errors, infos: infos,
      needsAge: !!(d.ageGate && alcohol.length && !ageOk),
      dateBlocked: errors.indexOf(d.messages.preorderDate.replace('[from]', d.preorderFrom)) !== -1
    };
  }

  // While the date rule is blocking, the only way out is the customer changing the date in the
  // Zapiet widget — which updates cart attributes without re-rendering the cart. Poll for that.
  var datePoller = null;
  function refreshFromCart() {
    fetch('/cart.js', { credentials: 'same-origin' }).then(function (res) { return res.json(); }).then(function (cart) {
      var attrs = cart.attributes || {};
      document.querySelectorAll('#yff-cart-rules-data').forEach(function (el) {
        try {
          var d = JSON.parse(el.textContent);
          d.deliveryDate = attrs['Delivery-Date'];
          d.checkoutMethod = attrs['Checkout-Method'];
          el.textContent = JSON.stringify(d);
        } catch (e) {}
      });
      apply();
    }).catch(function () {});
  }
  function watchDate(blocked) {
    if (blocked && !datePoller) datePoller = setInterval(refreshFromCart, 4000);
    if (!blocked && datePoller) { clearInterval(datePoller); datePoller = null; }
  }

  function render(result) {
    watchDate(!!result.dateBlocked);
    // Drawer: infos go in the box under the order summary (.yff-cart-rules--info), errors stay by the Checkout button.
    // Cart page has a single box, which shows both.
    var hasInfoBox = !!document.querySelector('.yff-cart-rules--info');
    document.querySelectorAll('.yff-cart-rules').forEach(function (box) {
      var html = '';
      var isInfoBox = box.classList.contains('yff-cart-rules--info');
      if (!isInfoBox) result.errors.forEach(function (m) { html += '<p class="yff-cart-rules__msg yff-cart-rules__msg--error" role="alert">' + m + '</p>'; });
      if (isInfoBox || !hasInfoBox) result.infos.forEach(function (m) { html += '<p class="yff-cart-rules__msg yff-cart-rules__msg--info">' + m + '</p>'; });
      // idempotent: the drawer is watched by MutationObservers, so only touch the DOM on change
      if (box.innerHTML !== html) box.innerHTML = html;
      if (box.hidden !== (html === '')) box.hidden = html === '';
    });
    document.querySelectorAll(CHECKOUT_SELECTORS).forEach(function (btn) {
      if (!result.ok) {
        btn.setAttribute('disabled', 'disabled');
        btn.dataset.yffBlocked = '1';
      } else if (btn.dataset.yffBlocked) {
        delete btn.dataset.yffBlocked;
        // only re-enable if nothing else (gift name check) is holding it
        if (!btn.dataset.yffHold) btn.removeAttribute('disabled');
      }
    });
  }

  function apply() {
    var d = readData();
    if (!d) return { ok: true, errors: [], infos: [], needsAge: false };
    var r = evaluate(d);
    render(r);
    return r;
  }

  // ---- 18+ modal
  function openAgeGate(onYes) {
    var modal = document.getElementById('yff-age-gate');
    if (!modal) { onYes(); return; }
    modal.hidden = false;
    document.body.classList.add('yff-age-gate-open');
    var yes = modal.querySelector('[data-age-yes]');
    var no = modal.querySelector('[data-age-no]');
    function close() {
      modal.hidden = true;
      document.body.classList.remove('yff-age-gate-open');
      yes.removeEventListener('click', onYesClick);
      no.removeEventListener('click', onNoClick);
      document.removeEventListener('keydown', onKey);
    }
    function onYesClick() { try { sessionStorage.setItem(AGE_KEY, '1'); } catch (e) {} close(); onYes(); }
    function onNoClick() { close(); modal.dispatchEvent(new CustomEvent('yff:age-declined')); }
    function onKey(e) { if (e.key === 'Escape') onNoClick(); }
    yes.addEventListener('click', onYesClick);
    no.addEventListener('click', onNoClick);
    document.addEventListener('keydown', onKey);
    yes.focus();
  }

  /* gate(proceed): re-check rules, then run proceed() only when allowed. */
  function gate(proceed) {
    var r = apply();
    if (!r.ok) {
      var box = document.querySelector('.yff-cart-rules:not([hidden])');
      if (box) { box.classList.remove('yff-shake'); void box.offsetWidth; box.classList.add('yff-shake'); }
      return false;
    }
    if (r.needsAge) { openAgeGate(proceed); return false; }
    proceed();
    return true;
  }

  window.YFFCartRules = { apply: apply, gate: gate, evaluate: evaluate };

  // Cart page: the submit button can be intercepted by app embeds (Zapiet) before the
  // form's submit event fires, so gate the click itself (capture phase) as well.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('#CartDrawer-Checkout, #checkout, button.cart__checkout-button[type="submit"]') : null;
    if (!btn || !document.getElementById('yff-cart-rules-data')) return;
    var d = readData();
    var r = apply();
    var hasPreorder = !!(d && d.items && d.items.some(function (i) { return i.preorder; }));
    if (!r.ok || r.needsAge) {
      e.preventDefault();
      e.stopImmediatePropagation();
      gate(function () { goToCheckout(); });
    } else if (hasPreorder) {
      // the rendered JSON can be stale after the Zapiet widget changes the date — re-read cart attributes first.
      // Re-dispatching the click afterwards gets swallowed by Zapiet's own handler, so navigate directly (as gate() does).
      e.preventDefault();
      e.stopImmediatePropagation();
      fetch('/cart.js', { credentials: 'same-origin' }).then(function (res) { return res.json(); }).then(function (cart) {
        d.deliveryDate = cart.attributes && cart.attributes['Delivery-Date'];
        d.checkoutMethod = cart.attributes && cart.attributes['Checkout-Method'];
        // keep the on-page JSON in step so later apply() calls (drawer re-renders) reach the same verdict
        document.querySelectorAll('#yff-cart-rules-data').forEach(function (el) { el.textContent = JSON.stringify(d); });
        var fresh = evaluate(d);
        render(fresh);
        if (!fresh.ok) {
          var box = document.querySelector('.yff-cart-rules:not([hidden])');
          if (box) { box.classList.remove('yff-shake'); void box.offsetWidth; box.classList.add('yff-shake'); }
          return;
        }
        if (fresh.needsAge) { openAgeGate(function () { goToCheckout(); }); return; }
        goToCheckout();
      }).catch(function () { goToCheckout(); });
    }
  }, true);

  // Cart page: native form submit → gate it
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form && form.id === 'cart' && document.getElementById('yff-cart-rules-data')) {
      var r = apply();
      if (!r.ok || r.needsAge) {
        e.preventDefault();
        gate(function () {
          try { sessionStorage.setItem(AGE_KEY, '1'); } catch (err) {}
          goToCheckout();
        });
      }
    }
  }, true);

  // Re-run whenever the drawer re-renders (it is reloaded via jQuery .load and Dawn's renderContents)
  var t;
  function scheduleApply() { clearTimeout(t); t = setTimeout(function () { apply(); remountZapiet(); }, 60); }
  function boot() {
    apply();
    remountZapiet();
    var drawer = document.querySelector('cart-drawer');
    if (drawer && window.MutationObserver) {
      new MutationObserver(scheduleApply).observe(drawer, { childList: true, subtree: true });
    }
    document.addEventListener('cart:refresh', scheduleApply);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
